import { useEffect, useState } from "react";
import { AlertTriangle, ClipboardList } from "lucide-react";
import { http, roleLabels, can } from "../api";
import { PanelHead, Loading } from "./common";
import { SaPerformancePanel } from "./SaAdmin";
import { SaPresenceWidget } from "./SyncAdmin";

export default function Dashboard({ user, onNavigate }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    http.get("/dashboard").then((r) => setData(r.data));
  }, []);

  if (!data) return <Loading>Loading workspace…</Loading>;

  const m = data.metrics;
  const cards = [
    { label: "Total leads", value: m.total_leads, sub: "In your scope", color: "green" },
    { label: "Active pipeline", value: m.active_leads, sub: "In motion", color: "red" },
    { label: "Applications", value: m.applications, sub: "Under processing", color: "blue" },
    { label: "Disbursements", value: m.disbursements, sub: "Month to date", color: "amber" },
  ];

  const greeting = new Date().getHours() < 12 ? "Good morning" : new Date().getHours() < 18 ? "Good afternoon" : "Good evening";

  return (
    <>
      <div className="page-intro">
        <div>
          <p className="eyebrow">
            {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}
          </p>
          <h1>
            {greeting}, {user.name.split(" ")[0]}.
          </h1>
          <p className="muted">Here’s the pulse of your {data.scope} today.</p>
        </div>
        <span data-testid="dashboard-scope-label" className="scope-chip">
          <span className="scope-dot" /> {roleLabels[user.role]} · {data.scope}
        </span>
      </div>

      <div className="metric-grid">
        {cards.map((c) => (
          <div
            data-testid={`metric-${c.label.toLowerCase().replaceAll(" ", "-")}`}
            className="metric"
            key={c.label}
          >
            <div className={`metric-icon ${c.color}`}>
              <ClipboardList size={17} />
            </div>
            <p>{c.label}</p>
            <strong>{c.value}</strong>
            <small>{c.sub}</small>
          </div>
        ))}
      </div>

      <div className="dashboard-grid">
        <section className="panel pipeline">
          <PanelHead title="Lead pipeline" action="View all leads" onClick={() => onNavigate("leads")} />
          <div className="pipeline-bars">
            {data.pipeline.map((x, i) => (
              <div className="bar-row" key={x.label}>
                <span>{x.label}</span>
                <div className="bar-track">
                  <i
                    style={{
                      width: `${Math.max(8, (x.value / Math.max(...data.pipeline.map((y) => y.value), 1)) * 100)}%`,
                      animationDelay: `${i * 80}ms`,
                    }}
                  />
                </div>
                <b>{x.value}</b>
              </div>
            ))}
          </div>
        </section>

        <section className="panel followups">
          <PanelHead title="Today’s follow-ups" action="Open leads" onClick={() => onNavigate("leads")} />
          <div className="followup-count">
            <strong>{m.todays_followups}</strong>
            <span>scheduled today · {m.overdue_followups} overdue</span>
          </div>
          {data.followups.length ? (
            data.followups.map((x) => (
              <div className="activity-row" data-testid="dashboard-followup-row" key={x.lead_id + x.due_at}>
                <span className="time">{x.due_at?.slice(11, 16)}</span>
                <div>
                  <strong>{x.followup_type}</strong>
                  <small>{x.notes || "Customer follow-up"}</small>
                </div>
              </div>
            ))
          ) : (
            <div className="empty">No follow-ups due today.</div>
          )}
        </section>
      </div>

      <section className="quick-strip">
        <div>
          <p className="eyebrow">NEXT ACTIONS</p>
          <h3>Keep the queue moving.</h3>
        </div>
        <div className="quick-actions">
          <button data-testid="quick-add-followup-button" onClick={() => onNavigate("leads")}>
            ＋ Add follow-up
          </button>
          {can(user, "view_reports") && (
            <button data-testid="quick-view-reports-button" onClick={() => onNavigate("reports")}>
              ↗ Review reports
            </button>
          )}
          {can(user, "upload_documents") && (
            <button data-testid="quick-upload-document-button" onClick={() => onNavigate("documents")}>
              ↑ Upload document
            </button>
          )}
        </div>
      </section>

      {data.team_pulse && (
        <section className="panel team-pulse-panel" data-testid="team-pulse-panel">
          <PanelHead
            title={`Team pulse${data.team_pulse.idle_count ? ` · ${data.team_pulse.idle_count} idle` : ""}`}
            action="Open roster"
            onClick={() => onNavigate("roster")}
          />
          {data.team_pulse.idle_members.length === 0 ? (
            <div className="empty compact-empty">
              Everyone with an active pipeline has moved something in the last 2 working days. 🙌
            </div>
          ) : (
            <ul className="idle-list">
              {data.team_pulse.idle_members.map((m) => (
                <li key={m.id} data-testid={`dashboard-idle-${m.id}`}>
                  <div className="idle-name">
                    <AlertTriangle size={13} />
                    <div>
                      <strong>{m.name}</strong>
                      <small>
                        {m.role === "back_office_executive" ? "Back office · " : "Telecaller · "}
                        {m.team_name || "Unassigned"}
                      </small>
                    </div>
                  </div>
                  <div className="idle-count">
                    <strong>{m.idle_working_days >= 999 ? "Never" : m.idle_working_days}</strong>
                    <span>{m.idle_working_days >= 999 ? "acted" : "days idle"}</span>
                  </div>
                  <div className="idle-load">
                    <strong>{m.active_leads}</strong>
                    <span>active leads</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {can(user, "view_sa_performance") && (
        <>
          <SaPresenceWidget compact />
          <SaPerformancePanel days={30} compact />
        </>
      )}
    </>
  );
}
